/**
 * Endpoints del Asistente IA.
 *
 * El navegador nunca habla con el proveedor de IA: todo pasa por EDSuite API,
 * que guarda la API key y ejecuta las herramientas de sólo lectura.
 */

import { apiClient } from '@/lib/http/api-client';

import type {
  AccionSpec,
  AlertasRespuesta,
  AsistenteConsumo,
  AsistenteStatus,
  ChatResponse,
  ConversacionDetalle,
  ConversacionResumen,
  HistoryEntry,
  Sugerencia,
} from './asistente.types';

export const asistenteKeys = {
  all: ['asistente'] as const,
  status: ['asistente', 'status'] as const,
  sugerencias: ['asistente', 'sugerencias'] as const,
  alertas: ['asistente', 'alertas'] as const,
  consumo: ['asistente', 'consumo'] as const,
  conversaciones: (buscar = '') => ['asistente', 'conversaciones', buscar] as const,
  conversacion: (id: number) => ['asistente', 'conversacion', id] as const,
};

export interface ChatRequest {
  message: string;
  history: HistoryEntry[];
  /** Conversación que se continúa (el backend abre una nueva si falta). */
  conversacionId?: number | null;
  empresa?: string | null;
  sucursal?: string | null;
}

export const asistenteApi = {
  /** Estado del servicio (si está habilitado y con qué modelo). */
  status: (signal?: AbortSignal): Promise<AsistenteStatus> =>
    apiClient.get<AsistenteStatus>('/api/asistente_status', { signal }),

  /** Consultas sugeridas para arrancar la conversación. */
  sugerencias: (signal?: AbortSignal): Promise<Sugerencia[]> =>
    apiClient.get<Sugerencia[]>('/api/asistente_sugerencias', { signal }),

  /**
   * Consulta en lenguaje natural.
   * El modelo puede tardar (bucle de herramientas), por eso el timeout es amplio.
   */
  chat: (payload: ChatRequest, signal?: AbortSignal): Promise<ChatResponse> =>
    apiClient.post<ChatResponse>('/api/asistente_chat', {
      body: {
        message: payload.message,
        history: payload.history,
        conversacion_id: payload.conversacionId ?? null,
        empresa: payload.empresa ?? null,
        sucursal: payload.sucursal ?? null,
      },
      signal,
      timeout: 90_000,
      retryOnAuthError: false,
    }),

  /** Historial del usuario (búsqueda por título o por contenido). */
  conversaciones: (buscar = '', signal?: AbortSignal): Promise<ConversacionResumen[]> =>
    apiClient.get<ConversacionResumen[]>(
      `/api/asistente_conversaciones${buscar ? `?buscar=${encodeURIComponent(buscar)}` : ''}`,
      { signal },
    ),

  /** Una conversación con todos sus turnos. */
  conversacion: (id: number, signal?: AbortSignal): Promise<ConversacionDetalle> =>
    apiClient.get<ConversacionDetalle>(`/api/asistente_conversaciones/${id}`, { signal }),

  renombrarConversacion: (id: number, titulo: string): Promise<unknown> =>
    apiClient.patch(`/api/asistente_conversaciones/${id}`, { body: { titulo } }),

  borrarConversacion: (id: number): Promise<unknown> =>
    apiClient.delete(`/api/asistente_conversaciones/${id}`),

  /** Avisos del momento para ofrecerlos como preguntas de un clic. */
  alertas: (signal?: AbortSignal): Promise<AlertasRespuesta> =>
    apiClient.get<AlertasRespuesta>('/api/asistente_alertas', { signal }),

  /** Oculta un aviso al usuario (vuelve a mostrarse pasados unos días). */
  descartarAlerta: (tipo: string): Promise<unknown> =>
    apiClient.post('/api/asistente_alertas/descartar', { body: { tipo }, retryOnAuthError: false }),

  /** Vuelve a mostrar los avisos descartados. */
  restaurarAlertas: (): Promise<unknown> =>
    apiClient.post('/api/asistente_alertas/restaurar', { body: {}, retryOnAuthError: false }),

  /**
   * Ejecuta una acción ya confirmada por el usuario.
   * El asistente sólo la propone: sin esta llamada no se escribe nada.
   */
  ejecutarAccion: (accion: Pick<AccionSpec, 'id' | 'params'>): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>('/api/asistente_accion', {
      body: { id: accion.id, params: accion.params },
      retryOnAuthError: false,
    }),

  /** Valoración de una respuesta (alimenta la mejora del asistente). */
  feedback: (consultaId: number, util: boolean, comentario?: string): Promise<unknown> =>
    apiClient.post('/api/asistente_feedback', {
      body: { consulta_id: consultaId, util, comentario },
      retryOnAuthError: false,
    }),

  /** Consumo de IA del usuario (tokens y consultas del mes). */
  consumo: (signal?: AbortSignal): Promise<AsistenteConsumo> =>
    apiClient.get<AsistenteConsumo>('/api/asistente_consumo', { signal }),
} as const;
